import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

// Bundle English (default) - always available instantly
import enTranslations from './locales/en/en.json'

// ============================================================================
// Constants
// ============================================================================

export const LANGUAGES = ['en', 'de', 'es'] as const
export type Language = (typeof LANGUAGES)[number]
export type TranslationFunctionType = (key: string) => string

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
}

// ============================================================================
// Loading State Management
// ============================================================================

const loadedLanguages = new Set<Language>(['en'])
let isLoading = false

type LoadingCallback = (loading: boolean) => void
const loadingCallbacks = new Set<LoadingCallback>()

/**
 * Subscribe to loading state changes
 * @returns Unsubscribe function
 */
export function onLoadingChange(callback: LoadingCallback): () => void {
  loadingCallbacks.add(callback)
  return () => loadingCallbacks.delete(callback)
}

function setLoading(loading: boolean): void {
  isLoading = loading
  loadingCallbacks.forEach((cb) => cb(loading))
}

/**
 * Check if language is currently being loaded
 */
export function isLanguageLoading(): boolean {
  return isLoading
}

/**
 * Check if a language has been loaded
 */
export function isLanguageLoaded(lang: Language): boolean {
  return loadedLanguages.has(lang)
}

// ============================================================================
// Language Switching
// ============================================================================

/**
 * Change language with lazy loading
 *
 * - English: instant (bundled)
 * - German/Spanish: loaded on first use, then cached
 *
 * @example
 * await changeLanguage('de')
 */
export async function changeLanguage(lang: Language): Promise<void> {
  if (!LANGUAGES.includes(lang)) {
    console.warn(`[i18n] Unsupported language: ${lang}`)
    return
  }

  if (lang === i18n.language && loadedLanguages.has(lang)) return

  // Load language resources if not already loaded
  if (!loadedLanguages.has(lang)) {
    setLoading(true)
    try {
      const translations = await import(`./locales/${lang}/${lang}.json`)

      // Add all translations under 'translation' namespace
      i18n.addResourceBundle(lang, 'translation', translations.default)

      loadedLanguages.add(lang)
    } catch (error) {
      console.error(`[i18n] Failed to load language: ${lang}`, error)
      setLoading(false)
      return
    }
    setLoading(false)
  }

  await i18n.changeLanguage(lang)
}

// ============================================================================
// i18n Initialization
// ============================================================================

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslations,
      },
    },
    supportedLngs: ['en', 'de', 'es'],
    fallbackLng: 'en',
    defaultNS: 'translation',
    ns: ['translation'],
    interpolation: {
      escapeValue: false, // React handles escaping
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    // Convert 'en-US' to 'en', 'de-DE' to 'de', etc.
    load: 'languageOnly',
  })

// ============================================================================
// Auto-load Saved Language
// ============================================================================

// Load saved language preference on startup (if not English)
const savedLang = localStorage.getItem('i18nextLng') as Language | null
if (savedLang && savedLang !== 'en' && LANGUAGES.includes(savedLang)) {
  changeLanguage(savedLang)
}

export default i18n
